/**
 * Done. / ChiroCPD — Shared Frontend TypeScript Types
 *
 * Single source of truth for all types used by frontend components,
 * hooks, contexts, and pages. Mirrors worker/src/types.ts where relevant.
 *
 * Naming: PascalCase for types/interfaces, SCREAMING_SNAKE for constants.
 */

// ── Auth ──────────────────────────────────────────────────────────────────────

export type SubscriptionStatus = 'active' | 'inactive' | 'past_due' | 'cancelled';

export interface User {
  id: string;
  email: string;
  name: string | null;
  professionId: string;
  subscriptionStatus: SubscriptionStatus;
  subscriptionExpiresAt: string | null;
  surveyCompleted: boolean;
  cohort: string | null;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// ── Cap Status ────────────────────────────────────────────────────────────────

export interface CapStatus {
  annualHoursLogged: number;
  annualHourCap: number;
  cycleHoursLogged: number;
  cycleHourCap: number;
  aiGenerationsUsed: number;
  aiGenerationCap: number;
  currentYear: number;
  cycleStartYear: number;
  isHourCapReached: boolean;
  isAiCapReached: boolean;
}

export interface CapStatusState {
  cap: CapStatus | null;
  loading: boolean;
  error: string | null;
}

// ── Activities ────────────────────────────────────────────────────────────────

export interface Activity {
  id: string;
  userId: string;
  professionId: string;
  activityTypeId: string;
  activityTypeName: string;
  title: string;
  description: string | null;
  hours: number;
  activityDate: string;
  hasReflection: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityType {
  id: string;
  professionId: string;
  name: string;
  category: string | null;
  sortOrder: number;
}

export interface CreateActivityInput {
  activityTypeId: string;
  title: string;
  description?: string | null;
  hours: number;
  activityDate: string;
}

export interface UpdateActivityInput {
  activityTypeId?: string;
  title?: string;
  description?: string | null;
  hours?: number;
  activityDate?: string;
}

// ── Reflections ───────────────────────────────────────────────────────────────

export interface Reflection {
  id: string;
  activityId: string;
  userId: string;
  outcomeText: string;
  reflectionText: string;
  aiGenerated: boolean;
  createdAt: string;
  updatedAt: string;
}

export type ReflectionGenerationState =
  | 'idle'
  | 'generating'
  | 'draftReady'
  | 'capReached'
  | 'error';

// ── Export / Portal Fields ────────────────────────────────────────────────────

export type ExportFieldStatus = 'complete' | 'partial' | 'empty';

export interface ExportFieldData {
  fieldName: string;
  fieldLabel: string;
  fieldOrder: number;
  fieldType: string;
  value: string;
  status: ExportFieldStatus;
  aiGenerated: boolean;
  guidanceText?: string;
  actionHref?: string;
}

export interface PortalFieldMapping {
  fieldName: string;
  fieldLabel: string;
  fieldOrder: number;
  fieldType: 'text' | 'textarea' | 'date' | 'number' | 'select';
  description: string;
  stubContent: string;
  version: string;
}

// ── Annual Plan ───────────────────────────────────────────────────────────────

export interface AnnualPlan {
  id: string;
  userId: string;
  year: number;
  planText: string;
  aiGenerated: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Dashboard ────────────────────────────────────────────────────────────────

export interface DashboardData {
  capStatus: CapStatus;
  recentActivities: Activity[];
  annualPlan: AnnualPlan | null;
}

// ── Survey ────────────────────────────────────────────────────────────────────

export type PracticeType = 'solo' | 'small_practice' | 'large_practice' | 'locum' | 'academic' | 'other';

export interface SurveyData {
  name: string;
  yearsRegistered: number;
  practiceType: PracticeType;
  specialInterests?: string;
  weakAreas?: string;
  goalsNextYear?: string;
  culturalSafetyFocus?: string;
}

// ── API Response wrappers ─────────────────────────────────────────────────────

export interface ApiError {
  error: string;
  code?: string;
}

export interface MutationResponse<T = unknown> {
  data: T;
  capStatus: CapStatus;
}

// ── Forms ────────────────────────────────────────────────────────────────────

export interface FormError {
  field: string;
  message: string;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type CapUnit = 'hours' | 'generations';
export type CapVariant = 'inline' | 'badge';
export type HealthStatus = 'healthy' | 'warning' | 'exhausted';

// ── Constants ────────────────────────────────────────────────────────────────

export const CPD_HOURS_ANNUAL = 20 as const;
export const CPD_HOURS_CYCLE = 50 as const;
export const AI_CAP_PER_MONTH = 5 as const;
export const PORTAL_FIELD_COUNT = 8 as const;
export const COPY_REVERT_MS = 2000 as const;
export const EXPORT_CACHE_TTL_MS = 30_000 as const;
